import { AssertionError } from 'assert';
import { PopupOffersOfType, PopupOfferType, PopupOfferTypeMap } from './PopupOffers';
import { cached } from 'Utility/async';
import Game from 'Game';
import { IAPReward } from 'Utility/RewardHelper';
import { GameActions } from 'State/Game/GameActions';
import { ResponseStatus, ResponseType } from 'State/Game/ContextType';
import { ProductId } from 'Utility/Product';
import { report } from 'report';
import { IOfferData } from './Offers/IOfferData';
import { OfferType } from './Offers/OfferTypes/OfferType';
import { OfferTier } from './Offers/Offers';
import { IGameState } from 'State/Game/IGameState';
import { getTotalRealCurrencySpent, IAPStateHelper } from 'State/Player/IAP/IAPStateHelper';
import { unexpectedValue } from 'Utility/Types';
import { MathHelper } from 'Utility/MathHelper';

const PAYER_REACTIVATION_OFFER_KEYS = Object.keys(PopupOfferTypeMap[PopupOfferType.PAYER_REACTIVATION_OFFER]);

const testOfferData: IOfferData = {
    tiers: [
        {
            type: OfferType.MULTI,
            products: [{ productId: ProductId.COINS_10K }],
            requirements: {
                max: 0,
            },
        },
        {
            type: OfferType.SINGLE,
            productId: ProductId.COINS_10K,
            requirements: {
                max: 10,
            },
        },
        {
            type: OfferType.SINGLE,
            productId: ProductId.COINS_10K,
        },
    ],
};
export namespace IAPHelper {
    export function isValidPayerReactivationOffer(
        val: any
    ): asserts val is keyof PopupOffersOfType<PopupOfferType.PAYER_REACTIVATION_OFFER> {
        //If key exists assert type else throw error
        if (!PAYER_REACTIVATION_OFFER_KEYS.includes(val)) {
            throw new AssertionError({ message: 'Not a valid PayerReactivationOffer' });
        }
    }

    export const CACHED_IAP_REWARDS_MAP = cached(() => {
        const iapRewards = Game.resource.get<IAPReward[]>('IAPRewards') ?? [];

        if (iapRewards.length === 0) {
            report('No IAPRewards found', {
                once: true,
                sampleRate: 0.5,
                fingerprint: 'NoIAPRewardsFound:CACHED_IAP_REWARDS_MAP',
            });
        }

        return Object.fromEntries(iapRewards.map(r => [r.id, r]));
    });

    /**
     * Waits for a restoreContext response that matches the provided productId
     * @param productId
     * @returns
     */
    export async function waitForPurchaseResponse(productId: ProductId): Promise<boolean> {
        // Wait for context to be restored
        while (true) {
            const result = await Game.store.waitFor(GameActions.restoreContext);

            // If not the correct response then continue to wait
            if (result.payload.type !== ResponseType.PURCHASE_PRODUCT || result.payload.data.product.id !== productId)
                continue;

            return result.payload.status !== ResponseStatus.FAIL;
        }
    }

    export function getRelevantOfferTier(state: IGameState, offerData: IOfferData): OfferTier {
        const currentTier = offerData.tiers.find(tierData => {
            switch (tierData.type) {
                case OfferType.SINGLE:
                    return IAPStateHelper.hasPurchasedProduct(state, tierData.productId);
                case OfferType.MULTI:
                    return tierData.products.some(product =>
                        IAPStateHelper.hasPurchasedProduct(state, product.productId)
                    );
                default:
                    unexpectedValue(tierData, false);
            }
        });

        if (currentTier) {
            return currentTier;
        }

        const totalRealCurrencySpent = getTotalRealCurrencySpent(state);

        return offerData.tiers.find(offerTier => {
            return MathHelper.isWithinRangeLimit(totalRealCurrencySpent, {
                min: offerTier?.requirements?.min ?? -Infinity,
                max: offerTier?.requirements?.max ?? Infinity,
            });
        });
    }
}
